import React from "react";
import { Checkbox } from "@shared/ui/checkbox";
import { Lock, FileSignature, Bell } from "lucide-react";
import { cn } from "@shared/lib/utils";

interface ClosureRequirementItemProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  icon: React.ReactNode;
  isDisabled?: boolean;
}

const ClosureRequirementItem = ({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  icon,
  isDisabled,
}: ClosureRequirementItemProps) => {
  return (
    <div
      className={cn(
        "flex items-start gap-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer",
        checked
          ? "bg-green-50/50 border-green-200 shadow-sm"
          : "bg-white border-neutral-100 hover:border-neutral-200",
      )}
      onClick={() => onCheckedChange(!checked)}
    >
      <div
        className={cn(
          "p-2 rounded-lg shrink-0",
          checked
            ? "bg-green-100 text-green-600"
            : "bg-neutral-100 text-neutral-400",
        )}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <label
          htmlFor={id}
          className={cn(
            "text-sm font-bold block mb-0.5 pointer-events-none",
            checked ? "text-green-800" : "text-neutral-700",
          )}
        >
          {label}
        </label>
        <p className="text-xs text-neutral-500 leading-relaxed pointer-events-none">
          {description}
        </p>
      </div>
      <Checkbox
        id={id}
        checked={checked}
        disabled={isDisabled}
        onCheckedChange={(val: boolean) => onCheckedChange(val)}
        className={cn(
          "h-5 w-5 rounded-md",
          checked ? "border-green-600 bg-green-600" : "border-neutral-300",
        )}
      />
    </div>
  );
};

interface ClosureRequirementsProps {
  requirements: {
    billsSent: boolean;
    readingsCollected: boolean;
    notificationsDone: boolean;
  };
  onUpdate: (requirements: any) => void;
  isDisabled?: boolean;
}

export const ClosureRequirements = ({
  requirements,
  onUpdate,
  isDisabled,
}: ClosureRequirementsProps) => {
  const handleChange = (key: string, val: boolean) => {
    onUpdate({ ...requirements, [key]: val });
  };

  return (
    <div className="bg-white p-6 rounded-xl border border-neutral-100 shadow-sm space-y-6">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
          <FileSignature className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-neutral-800">
            Closure Requirements
          </h3>
          <p className="text-xs text-neutral-500">
            Confirm all mandatory actions before marking as complete
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ClosureRequirementItem
          id="bills-sent"
          label="All Bills Sent"
          description="Confirm that generated bills have been dispatched to all consumers."
          icon={<Bell className="w-5 h-5" />}
          checked={requirements.billsSent}
          onCheckedChange={(val) => handleChange("billsSent", val)}
          isDisabled={isDisabled}
        />

        <ClosureRequirementItem
          id="readings-collected"
          label="Final Readings Collected"
          description="Verify that all final meter readings have been accurately recorded."
          icon={<Lock className="w-5 h-5" />}
          checked={requirements.readingsCollected}
          onCheckedChange={(val) => handleChange("readingsCollected", val)}
          isDisabled={isDisabled}
        />

        <ClosureRequirementItem
          id="notifications-done"
          label="Customer Notification"
          description="Ensure all required disconnection notices have been sent/confirmed."
          icon={<FileSignature className="w-5 h-5" />}
          checked={requirements.notificationsDone}
          onCheckedChange={(val) => handleChange("notificationsDone", val)}
          isDisabled={isDisabled}
        />
      </div>
    </div>
  );
};
